// packages/core/src/learning/school-modules/factor-investing.ts
// ─────────────────────────────────────────────────────────────────────────
// Lens Pack for the 'factor-investing' school. LearningModule, surface 'school'.
//   module id   = 'school-factor-investing'  (matches exhibit nativeModuleId)
//   homeSchool  = 'factor-investing'
// PROMOTED: three authored lessons (above the divider in the index).
//
// Placeholder presentational fields (emoji, accentColor, tier, order) carried
// from the stub conventions — tune per-school in the presentation pass.
// ─────────────────────────────────────────────────────────────────────────

import type { LearningModule } from '../LearningTypes';

export const SCHOOL_FACTOR_INVESTING_MODULE: LearningModule = {
	id: 'school-factor-investing',
	title: 'Factor Investing',
	description:
		'The lens that tilts toward studied market patterns — factors as patterns not promises, tracking error as the price, and rules that protect you from stories.',
	emoji: '🧮',
	accentColor: '#3a82c4',
	tier: 'growth',
	pillar: 'long-term-investing',
	order: 0,
	surface: 'school',
	homeSchool: 'factor-investing',
	lessons: [
		{
			id: 'school-factor-investing-01',
			title: 'Factors are patterns, not promises',
			description:
				'This lesson teaches why factor investing trusts studied patterns without pretending they always work.',
			pillar: 'long-term-investing',
			schoolsOfThought: ['factor-investing'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['factor-investing', 'factor-evidence', 'return-premium'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['factor evidence', 'pattern humility', 'return premium'],
			blocks: [
				{
					type: 'paragraph',
					text: 'Factor investing starts with a temptation most investors feel: the desire to find a smarter edge. But this lens does not want a hot tip, a favorite founder, or a confident prediction. It wants patterns that have been studied across time.',
				},
				{
					type: 'paragraph',
					text: 'Factor investing believes certain traits, like value, size, momentum, quality, and profitability, may explain why some groups of investments behave differently from the broad market. The lens is not saying these traits always win. It is saying they deserve attention because they have shown up often enough to study.',
				},
				{
					type: 'bullet_list',
					items: [
						'Evidence beats vibes',
						'Patterns can fade',
						'No factor works all the time',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is treating a factor like a cheat code instead of a risky pattern.',
				},
				{
					type: 'example',
					text: 'A value factor strategy may focus on cheaper companies. It can work for long periods, then trail exciting growth companies for years. The factor did not promise comfort. It only offered a disciplined reason to tilt.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that evidence can make the strategy sound cleaner than it feels. A factor can be backed by research and still disappoint you for a long stretch. The real test is whether the investor can live through that gap.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, connect this lens to behavior before math. Write down what would make you abandon a strategy, then ask whether that exit rule is based on evidence or emotion.',
				},
				{
					type: 'key_takeaway',
					text: 'Factor investing is evidence with humility, not certainty with a spreadsheet.',
				},
			],
			audioScript:
				'Factor investing begins with a very reasonable question: are there patterns in markets that are stronger than one person’s opinion? Instead of asking, “Which company do I personally love?” or “What stock is everyone talking about?” this lens asks, “Are there traits that have shown up across many companies, markets, and time periods?” That is the heart of the factor worldview.\n\nA factor is basically a trait. Value is a trait. Smaller companies can be a trait. Momentum is a trait. Quality is a trait. Profitability is a trait. The factor investor is not saying every company with one of these traits is automatically good. That would be too simple. The idea is that groups of investments with certain traits may behave differently from the market over time. Sometimes those differences may be rewarded. Sometimes they may not.\n\nThis lens protects investors from story driven investing. A lot of people build portfolios around narratives. This founder is brilliant. This company is the future. This sector is unstoppable. This trend is obvious. Factor investing is suspicious of that kind of confidence. It says, “Fine, but what does the evidence say?” It wants rules. It wants a repeatable method. It wants less personality and more process.\n\nBut factor investing has a serious emotional problem: evidence does not make the ride easy. A factor can be supported by research and still trail the market for years. That is not a small detail. That is the whole lived experience. Imagine choosing a value tilt because you believe cheaper companies may be rewarded over time. Then growth stocks dominate for years and your portfolio looks old, boring, and wrong. The research does not hug you at night. The backtest does not stop you from feeling foolish.\n\nThat is why the phrase “patterns, not promises” matters. Factor investing is not a cheat code. It does not mean you found a reliable way to win every year. It means you chose a disciplined exposure to a pattern that may or may not reward you over your actual investing life. The word “may” is important. If someone turns factor investing into certainty, they are abusing the lens.\n\nThe blind spot is complexity. The more factors you add, the smarter the strategy can sound. But smart sounding does not always mean livable. Some investors start with evidence and end up with a strategy they do not understand well enough to stick with. Others keep switching factors based on what recently worked, which turns the whole thing back into performance chasing.\n\nSo the useful question inside Nisk is not just, “Which factor is best?” A better question is, “What rule could I actually live with?” If a strategy underperformed for three years, would you still understand why you own it? If the answer is no, the issue may not be the factor. The issue may be the gap between research and your real behavior.',
			flashcards: [
				{
					id: 'school-factor-investing-01-fc-1',
					role: 'main_idea',
					front: 'What is a factor?',
					back: 'A studied trait that may help explain different market behavior.',
				},
				{
					id: 'school-factor-investing-01-fc-2',
					role: 'watch_out',
					front: 'What is the factor trap?',
					back: 'Treating a pattern like a promise or cheat code.',
				},
				{
					id: 'school-factor-investing-01-fc-3',
					role: 'use_it',
					front: 'What should you test first?',
					back: 'Whether you could live with the strategy when it trails.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Check your behavior rules',
				prompt: 'Write what would make you abandon a strategy, then ask whether that rule is emotional or evidence based.',
			},
			revision: 1,
		},
		{
			id: 'school-factor-investing-02',
			title: 'Tracking error is the price',
			description:
				'This lesson teaches why factor investing requires living with periods where your portfolio looks wrong.',
			pillar: 'long-term-investing',
			schoolsOfThought: ['factor-investing'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['factor-investing', 'tracking-error', 'strategy-patience'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'tracking error pain',
				'strategy patience',
				'benchmark discomfort',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'The hardest part of factor investing is not learning the vocabulary. It is watching a strategy you chose trail the simple market portfolio everyone else keeps talking about.',
				},
				{
					type: 'paragraph',
					text: 'Factor investing accepts tracking error. That means your results will look different from the broad market, sometimes better, sometimes worse. If you choose to be different, you have to survive feeling different.',
				},
				{
					type: 'bullet_list',
					items: [
						'Different can underperform',
						'Patience can feel embarrassing',
						'Rules need emotional backup',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is wanting factor results without factor discomfort.',
				},
				{
					type: 'example',
					text: 'An investor chooses a factor tilt because they believe in the evidence. Then the broad market wins for four years. Friends with simpler portfolios look smarter. The factor investor now has to decide whether they had a strategy or only a preference for sounding advanced.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that factor investing may add complexity without guaranteeing better outcomes. A simple broad market approach can be easier to understand, easier to stick with, and good enough for many people.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, test whether complexity matters less than your savings rate. A strategy that looks clever can still matter less than steady contributions, lower panic, and enough time.',
				},
				{
					type: 'key_takeaway',
					text: 'If you cannot handle tracking error, you probably cannot handle factor investing.',
				},
			],
			audioScript:
				'Tracking error sounds like a technical finance phrase, but emotionally it means something simple: your portfolio looks different from the thing everyone compares against. Sometimes that feels good. Sometimes it feels awful. Factor investing accepts that difference on purpose. That is both the appeal and the pain.\n\nIf you own a broad market portfolio, you mostly get the broad market experience. You may still have ups and downs, but you are not constantly asking why your strategy is so different from the main scoreboard. Factor investing changes that. If you tilt toward value, momentum, quality, size, or another factor, you are choosing to behave differently from the market. That means there will be times when your choice looks smart and times when it looks embarrassing.\n\nThis is where a lot of investors overestimate themselves. They like the idea of evidence. They like the idea of being systematic. They like the idea of having a strategy with research behind it. But when the factor trails for a few years, the emotional story changes. Now the broad market looks easier. Friends look smarter. Simple indexing looks cleaner. The investor starts asking whether the factor is broken, whether the research stopped working, or whether they should switch to whatever has been winning lately.\n\nThe factor lens says this discomfort is not a side issue. It is the price. If you want the chance of a different result, you have to accept the reality of a different path. You do not get to be different only during the years when different feels good. That is not a strategy. That is performance chasing with academic language.\n\nThe common mistake is wanting factor results without factor discomfort. People want value when value wins. They want momentum after momentum works. They want quality when quality is praised. But factor investing only makes sense if the rules survive the bad periods too. Otherwise the investor is not harvesting a pattern. They are just moving from story to story.\n\nThe blind spot is that tracking error may not be worth it for everyone. A simple broad market strategy can be perfectly respectable. It can be easier to understand, easier to explain, easier to hold, and easier to automate. Factor investing adds moving parts. More moving parts mean more places to doubt yourself. For many people, simplicity is not a weakness. It is the reason the plan survives.\n\nInside Nisk, this connects to a blunt question: does the added complexity change your real life enough to matter? If your savings rate is low, your emergency runway is thin, or your behavior is unstable, a factor tilt may be less important than the basics. A clever strategy cannot rescue poor consistency. Factor investing is strongest when it is owned by someone who understands the evidence, accepts the discomfort, and does not confuse complexity with maturity.',
			flashcards: [
				{
					id: 'school-factor-investing-02-fc-1',
					role: 'main_idea',
					front: 'What is tracking error?',
					back: 'Your strategy performing differently from the broad market.',
				},
				{
					id: 'school-factor-investing-02-fc-2',
					role: 'watch_out',
					front: 'What do factor investors underestimate?',
					back: 'The emotional pain of looking wrong for years.',
				},
				{
					id: 'school-factor-investing-02-fc-3',
					role: 'use_it',
					front: 'What should you compare first?',
					back: 'Whether strategy complexity matters more than savings rate and consistency.',
				},
			],
			applyAction: {
				type: 'analyzeSavingsRate',
				label: 'Check your savings rate',
			},
			revision: 1,
		},
		{
			id: 'school-factor-investing-03',
			title: 'Rules protect you from stories',
			description:
				'This lesson teaches how factor investing uses structure to reduce emotional stock picking and recent winner chasing.',
			pillar: 'long-term-investing',
			schoolsOfThought: ['factor-investing'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: [
				'factor-investing',
				'rules-over-stories',
				'systematic-discipline',
			],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'rules over stories',
				'systematic discipline',
				'factor misuse',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Markets are full of stories. A company has a mission. A sector has a future. A stock has momentum. A chart looks obvious. Factor investing does not trust your excitement by default.',
				},
				{
					type: 'paragraph',
					text: 'This lens uses rules to decide what belongs in the strategy. The rule might favor value, quality, momentum, size, or another studied trait. The point is to reduce emotional guessing and make the process repeatable.',
				},
				{
					type: 'bullet_list',
					items: [
						'Rules reduce impulse',
						'Process beats personality',
						'Rebalancing tests discipline',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is using factors as decoration for the decision you already wanted to make.',
				},
				{
					type: 'example',
					text: 'Someone says they are a factor investor, but they only choose the factor that performed best recently. That is not discipline. That is trend chasing wearing a research costume.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that rules can become a false sense of safety. A systematic strategy can still lose money, underperform, become crowded, or fail to fit the investor’s actual temperament.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, use the simulator to remember the bigger picture. Even a smart strategy still sits under the basics: income, savings rate, time, behavior, and whether the plan is livable.',
				},
				{
					type: 'key_takeaway',
					text: 'Factor investing works as a discipline only if the rules are real before emotions arrive.',
				},
			],
			audioScript:
				'Factor investing is partly a defense system against stories. That may sound cold, but it is useful. Investors are very good at explaining why they like something. A company has a great founder. A product feels inevitable. A sector sounds like the future. A stock has been going up. A chart looks clean. A friend made money. A podcast made the case sound obvious. Before long, the investor does not feel like they are guessing. They feel like they are seeing the truth.\n\nFactor investing does not trust that feeling. It says, “What rule are you following?” That question matters because rules create distance from emotion. A value rule might look for cheaper companies based on certain measures. A quality rule might look for stronger profitability or balance sheet traits. A momentum rule might look for recent relative strength. Different factors work differently, but the worldview is similar: define the exposure before your mood starts arguing.\n\nThis protects the investor from one of the oldest traps in markets: building a story after the desire is already there. People often decide what they want, then search for evidence to justify it. Factor investing tries to reverse that. It starts with the rule, then lets the rule choose. That can feel less exciting, but excitement is not the same as discipline.\n\nThe problem is that people can misuse factors too. Someone can say they believe in factor investing, then constantly switch to the factor that recently won. If momentum did well, they become a momentum person. If quality did well, they become a quality person. If value comes back, suddenly they rediscover value. That is not systematic investing. That is recent winner chasing with smarter vocabulary.\n\nRules only protect you if they are real before emotions arrive. A rule made during a calm period is different from a rule invented after a bad quarter. A rule you understand is different from a rule you copied. A rule you can explain is different from a rule you only respect because it sounded advanced.\n\nThe blind spot is that rules can create a false sense of safety. A systematic process can still lose money. It can still trail the market. It can still become crowded. It can still depend on assumptions that may not hold the same way in the future. It can still be wrong for your temperament. The existence of a rule does not make the outcome safe. It just makes the decision process more disciplined.\n\nInside Nisk, factor investing should sit inside the bigger money picture. Your income matters. Your savings rate matters. Your time horizon matters. Your runway matters. Your ability to stay calm matters. A factor tilt is not the foundation of your life. It is one possible interpretation of investing strategy. The strongest version of this lens is not “I found the perfect factor.” It is “I know what rule I am following, why I am following it, and what pain I agreed to tolerate.”',
			flashcards: [
				{
					id: 'school-factor-investing-03-fc-1',
					role: 'main_idea',
					front: 'Why do factors use rules?',
					back: 'Rules reduce emotional guessing and make the process repeatable.',
				},
				{
					id: 'school-factor-investing-03-fc-2',
					role: 'watch_out',
					front: 'How are factors misused?',
					back: 'Choosing the factor that recently won and calling it discipline.',
				},
				{
					id: 'school-factor-investing-03-fc-3',
					role: 'use_it',
					front: 'What should a rule survive?',
					back: 'Bad performance, market noise, and your own emotional pressure.',
				},
			],
			applyAction: {
				type: 'openSimulator',
				label: 'Simulate steady investing',
			},
			revision: 1,
		},
	],
};
